import InfoIcon from '@mui/icons-material/Info'
import { Box, Stack } from '@mui/material'
import { FormProvider } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import {
    getGetIdsHealthcareservicesHcsIdCaregiversQueryKey,
    useDeleteIdsHealthcareservicesHcsIdCaregiversUserId,
} from '@toktokdoctech/tokapi-client'
import { displaySnackbar } from '../../actions/main'
import useCurrentHcs from '../../hooks/useCurrentHcs'
import { RouterRouteId } from '../../routers/_props'
import { useAppDispatch } from '../../store'
import { ERROR_MESSAGE } from '../../utils/ttdError'
import CommonLoaderBackdrop from '../Common/CommonLoaderBackdrop'
import DeletionBanner from '../Common/DeletionBanner/DeletionBanner'
import FormBreak from '../Common/form/formBreak/FormBreak'
import FormContainer from '../Common/form/formContainer/FormContainer'
import SaveComponent from '../Common/SaveComponent/SaveComponent'
import SearchRpps from '../Common/searchRpps/SearchRpps'
import ActionDialog from '../feature/actionDialog/ActionDialog'
import NetworkCallout from '../feature/networkCallout/NetworkCallout'
import { UserData, UserFormProps } from './_props'
import sxs from './_sxs'
import Activated from './fields/Activated'
import { ContactPhone } from './fields/ContactPhone'
import { Email } from './fields/Email'
import FormSection from '../Common/form/formSection/FormSection'
import MentionsAndTitles from './fields/MentionAndTitles'
import PrivatePhone from './fields/PrivatePhone'
import SecuredEmail from './fields/SecuredEmail'
import UserFunction from './fields/UserFunction'
import UserRole from './fields/UserRole'
import IdentitySection from './sections/IdentitySection'
import ProfessionalSection from './sections/ProfessionalSection'
import useUserFormBase from './utils/useUserFormBase'
import { tanstackQueryClient } from '../../api/tanstackClient'

export const CaregiverForm = ({ mode, data, onSave }: UserFormProps) => {
    const dispatch = useAppDispatch()
    const currentHcs = useCurrentHcs()
    const params = useParams<RouterRouteId>()
    const userId = params.id ?? ''
    const hcsId = currentHcs.id

    const { mutate: deleteCaregiver, isPending: isDeletingCaregiver } =
        useDeleteIdsHealthcareservicesHcsIdCaregiversUserId({
            mutation: {
                onSuccess: () => {
                    update({ shouldBlockNavigation: false })
                    const caregiversList = getGetIdsHealthcareservicesHcsIdCaregiversQueryKey(hcsId)
                    tanstackQueryClient.invalidateQueries({ queryKey: caregiversList })

                    dispatch(displaySnackbar('success', "L'utilisateur a été supprimé avec succès"))
                    setShouldRedirect(true)
                },
                onError: () => {
                    dispatch(displaySnackbar('error', "Suppression de l'utilisateur impossible"))
                },
            },
        })

    const {
        form,
        state,
        update,
        editMode,
        certifiedAccessMode,
        isLoading,
        hasFormError,
        globalErrorMessage,
        setGlobaleErrorMessage,
        clearGlobaleErrorMessage,
        userRpps,
        searchRppsHasError,
        handleRppsChange,
        cancelButton,
        redirectButton,
        buildHandleSaveTrigger,
        buildHandleSave,
        buildHandleCancel,
        buildSaveButtonAction,
        setShouldRedirect,
    } = useUserFormBase({ mode, data, returnLocation: '/users' })

    const save = async (userData: UserData) => {
        update({
            isLoading: true,
            loaderMessage: editMode ? 'Enregistrement des modifications' : "Création de l'utilisateur",
        })
        try {
            await onSave(userData)

            update({ isLoading: false, loaderMessage: undefined })
            dispatch(
                displaySnackbar(
                    'success',
                    editMode ? 'Les modifications ont bien été enregistrées !' : "L'utilisateur a bien été ajouté !",
                ),
            )
            return true
        } catch (err) {
            console.error({ err })

            // Structured error from the tokapi backend ({ message: "..." }) — surface it inline
            // instead of a generic snackbar (the axios error's own message is just "Request failed…").
            const backendMessage = (err as any)?.response?.data?.message
            if (typeof backendMessage === 'string' && backendMessage.length > 0) {
                setGlobaleErrorMessage(backendMessage)
                update({ isLoading: false, loaderMessage: undefined })
                return undefined
            }

            if (err instanceof Error) {
                if (err.message === ERROR_MESSAGE.MISSING_ROLE) {
                    setGlobaleErrorMessage('Veuillez sélectionner un rôle')
                } else {
                    clearGlobaleErrorMessage()
                    dispatch(displaySnackbar('error', err.message))
                }
            }

            if (typeof err === 'string') {
                setGlobaleErrorMessage(err)
                dispatch(displaySnackbar('error', err))
            }

            const errAny = err as any
            if (errAny.code && [202, 203].includes(errAny.code)) {
                setGlobaleErrorMessage(
                    `Ce courriel est déjà associé à un compte existant. Vous pouvez saisir un autre courriel, ou annuler l'ajout de l'utilisateur en cours.`,
                )
            }

            update({ isLoading: false, loaderMessage: undefined })
            return undefined
        }
    }

    const handleSave = buildHandleSave(save)
    const handleSaveTrigger = buildHandleSaveTrigger(handleSave)
    const handleCancel = buildHandleCancel()

    const handleDelete = () => {
        deleteCaregiver({ hcsId, userId })
    }

    return (
        <>
            <FormProvider {...form}>
                {!data.rpps && (
                    <FormContainer sx={{ ...sxs.UserForm, marginY: '2.125rem' }}>
                        <SearchRpps
                            displayTitle
                            onRppsUserChange={handleRppsChange}
                            selectedValue={undefined}
                            codeprofession={undefined}
                            required={certifiedAccessMode}
                            error={searchRppsHasError && !userRpps}
                            errorMessage="Cette information est obligatoire et n'a pas été renseignée"
                        />
                    </FormContainer>
                )}
                <Box sx={sxs.UserFormContainer}>
                    <FormContainer sx={sxs.UserForm}>
                        <IdentitySection />
                        <ProfessionalSection>
                            <MentionsAndTitles />
                            <UserFunction />
                        </ProfessionalSection>
                        <FormSection label="Coordonnées de contact">
                            <Email />
                            <SecuredEmail readonly />
                            {editMode && <PrivatePhone />}
                            <ContactPhone />
                        </FormSection>
                        <FormSection label="Gestion de l'utilisateur">
                            <UserRole />
                            {editMode && <FormBreak size="0.5" />}
                            {editMode && <Activated />}
                        </FormSection>

                        <Stack alignItems="center" gap="1.5rem" sx={{ width: '100%' }}>
                            {!editMode && (
                                <Box sx={{ minWidth: '34rem', width: '75%', marginTop: '3.5rem' }}>
                                    <NetworkCallout text="Après enregistrement, l'identifiant de connexion et le mot de passe provisoire seront envoyés à l'utilisateur sur son courriel de contact" />
                                </Box>
                            )}
                            <SaveComponent
                                withMargin={false}
                                saveButton={{ action: buildSaveButtonAction(handleSaveTrigger), isLoading }}
                                cancelButton={{ action: handleCancel, displayed: currentHcs.isCurrentUserCertified }}
                                disableAll={isLoading}
                                shouldBlockNavigation={state.shouldBlockNavigation}
                                errorMessage={
                                    hasFormError || Boolean(globalErrorMessage) ? globalErrorMessage : undefined
                                }
                            />
                        </Stack>
                    </FormContainer>
                    {mode === 'EDITION' && (
                        <DeletionBanner
                            bannerTitle="Êtes-vous sûr de vouloir révoquer l’accès de l’utilisateur à l’établissement ?"
                            bannerLabel="Révoquer l’accès à l’établissement"
                            bannerSubLabel="Après confirmation, cette action sera définitive"
                            buttonName="Révoquer"
                            buttonText="Révoquer"
                            onConfirm={handleDelete}
                            isPending={isDeletingCaregiver}
                        />
                    )}
                </Box>
            </FormProvider>
            {isLoading && <CommonLoaderBackdrop open label={state.loaderMessage} />}
            <ActionDialog
                open={Boolean(state.redirectKey)}
                onClose={() => update({ redirectKey: undefined })}
                title={`Vous allez être redirigé vers l'interface de changement de ${
                    state?.redirectKey === 'email' ? 'courriel de contact' : 'numéro de téléphone'
                }`}
                content="En cas d'autres modifications apportées à votre compte, elles ne seront pas sauvegardées : assurez-vous de les enregistrer avant de poursuivre."
                cancelProps={cancelButton}
                saveProps={redirectButton}
                icon={<InfoIcon />}
            />
        </>
    )
}
